import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiShield,FiLock,FiEye,FiServer,FiCheckCircle,FiFileText,FiUsers,FiArrowRight}=FiIcons;

const Legal=()=> {
const [headerRef,headerInView]=useInView({triggerOnce: true,threshold: 0.1});
const [contentRef,contentInView]=useInView({triggerOnce: true,threshold: 0.1});

const legalSections=[
{
icon: FiEye,
title: 'Privacy & Data Protection',
content: [
'We follow least-privilege access and store only the data necessary to deliver the service',
'PII and customer data are handled per contract specifications',
'Data retention policies align with project requirements and legal obligations',
'No data is shared with third parties without explicit consent',
'Regular data audits ensure compliance with privacy standards'
],
gradient: 'from-blue-500 to-indigo-500',
link: '/privacy'
},
{
icon: FiServer,
title: 'Model & Vendor Choices',
content: [
'Models selected based on cost,privacy,and performance requirements',
'Local model options available for enhanced privacy and cost control',
'Support for multiple LLM providers (OpenAI,Anthropic,Google,local via Ollama)',
'Transparent about model capabilities and limitations',
'Regular evaluation of model performance and bias'
],
gradient: 'from-green-500 to-teal-500'
},
{
icon: FiLock,
title: 'Security & Compliance',
content: [
'Secrets stored in secure vaults with role-based access controls',
'All data encrypted at rest and in transit',
'Regular security assessments and penetration testing',
'Audit logs available on request for compliance purposes',
'SOC 2 Type II compliant infrastructure where applicable'
],
gradient: 'from-red-500 to-pink-500'
},
{
icon: FiFileText,
title: 'Terms of Service',
content: [
'Clear usage guidelines and acceptable use policies',
'Intellectual property rights and licensing terms',
'Service availability and performance commitments',
'Payment terms and billing procedures',
'Termination and dispute resolution procedures'
],
gradient: 'from-purple-500 to-pink-500',
link: '/terms'
}
];

const complianceStandards=[
'GDPR Compliance',
'SOC 2 Type II',
'ISO 27001 Guidelines',
'HIPAA Ready',
'Enterprise Security Standards'
];

const legalDocuments=[
{
title: 'Privacy Policy',
description: 'Comprehensive privacy policy covering data collection,usage,and protection',
icon: FiEye,
link: '/privacy',
updated: 'January 15,2025'
},
{
title: 'Terms of Use',
description: 'Terms and conditions governing the use of our services',
icon: FiFileText,
link: '/terms',
updated: 'January 15,2025'
},
{
title: 'Cookie Policy',
description: 'Information about how we use cookies and tracking technologies',
icon: FiServer,
link: '/privacy#cookies',
updated: 'January 15,2025'
}
];

return (
<div className="relative">
{/* Animated Background */}
<div className="absolute inset-0 overflow-hidden pointer-events-none">
<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
</div>

{/* Header */}
<section ref={headerRef} className="py-24 lg:py-32 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<motion.div
initial={{opacity: 0,y: 20}}
animate={headerInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6}}
className="text-center"
>
<h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8">
<span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
Legal & Privacy
</span>
</h1>
<p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
Our commitment to security,privacy,and compliance. We build trust through transparency and adherence to industry best practices.
</p>
</motion.div>
</div>
</section>

{/* Legal Documents */}
<section className="py-16 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<motion.div
initial={{opacity: 0,y: 20}}
animate={{opacity: 1,y: 0}}
transition={{duration: 0.6}}
className="text-center mb-12"
>
<h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">Legal Documents</h2>
<p className="text-xl text-gray-300 max-w-3xl mx-auto">
Access our comprehensive legal documentation and policies
</p>
</motion.div>

<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
{legalDocuments.map((doc,index)=> (
<motion.div
key={index}
initial={{opacity: 0,y: 20}}
animate={{opacity: 1,y: 0}}
transition={{duration: 0.6,delay: index * 0.1}}
whileHover={{y: -5,scale: 1.02}}
className="group"
>
<Link
to={doc.link}
className="block bg-dark-800/30 backdrop-blur-sm rounded-2xl p-8 border border-dark-700/30 hover:border-primary-500/30 transition-all duration-300 h-full"
>
<div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-purple rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
<SafeIcon icon={doc.icon} className="w-8 h-8 text-white" />
</div>
<h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-300 transition-colors">
{doc.title}
</h3>
<p className="text-gray-400 text-sm leading-relaxed mb-4">
{doc.description}
</p>
<div className="text-xs text-primary-400 font-medium bg-primary-500/10 px-3 py-2 rounded-lg inline-block border border-primary-500/20">
Updated: {doc.updated}
</div>
<div className="flex items-center text-primary-400 mt-4 group-hover:translate-x-2 transition-transform duration-300">
<span className="text-sm font-medium mr-2">Read More</span>
<SafeIcon icon={FiArrowRight} className="w-4 h-4" />
</div>
</Link>
</motion.div>
))}
</div>
</div>
</section>

{/* Legal Sections */}
<section ref={contentRef} className="py-20 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="space-y-16">
{legalSections.map((section,index)=> (
<motion.div
key={index}
initial={{opacity: 0,y: 20}}
animate={contentInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6,delay: index * 0.2}}
className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-dark-700/30"
>
<div className="flex items-center mb-8">
<div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${section.gradient} rounded-2xl mr-4 shadow-lg`}>
<SafeIcon icon={section.icon} className="w-8 h-8 text-white" />
</div>
<div className="flex-1">
<h2 className="text-3xl lg:text-4xl font-bold text-white">{section.title}</h2>
{section.link && (
<Link
to={section.link}
className="inline-flex items-center text-primary-400 hover:text-primary-300 font-medium mt-2 transition-colors"
>
View Full Policy
<SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1" />
</Link>
)}
</div>
</div>

<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
<div className="space-y-4">
{section.content.map((item,itemIndex)=> (
<div key={itemIndex} className="flex items-start">
<SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
<p className="text-gray-300">{item}</p>
</div>
))}
</div>

<div className="bg-dark-700/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-600/30">
<h3 className="text-lg font-semibold text-white mb-4">Key Benefits</h3>
<ul className="space-y-2 text-gray-300">
{section.title==='Privacy & Data Protection' && (
<>
<li>• Minimal data collection</li>
<li>• Transparent data usage</li>
<li>• Right to data deletion</li>
<li>• Regular compliance audits</li>
</>
)}
{section.title==='Model & Vendor Choices' && (
<>
<li>• Cost optimization</li>
<li>• Privacy-first options</li>
<li>• Performance transparency</li>
<li>• Vendor flexibility</li>
</>
)}
{section.title==='Security & Compliance' && (
<>
<li>• Enterprise-grade security</li>
<li>• End-to-end encryption</li>
<li>• Access monitoring</li>
<li>• Incident response</li>
</>
)}
{section.title==='Terms of Service' && (
<>
<li>• Clear usage guidelines</li>
<li>• Fair billing practices</li>
<li>• IP protection</li>
<li>• Dispute resolution</li>
</>
)}
</ul>
</div>
</div>
</motion.div>
))}
</div>
</div>
</section>

{/* Compliance Standards */}
<section className="py-20 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="text-center mb-12">
<h2 className="text-4xl lg:text-5xl font-bold mb-6">
<span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
Compliance Standards
</span>
</h2>
<p className="text-xl text-gray-300 max-w-3xl mx-auto">
We adhere to industry-leading security and privacy standards to ensure your data and applications are protected.
</p>
</div>

<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
{complianceStandards.map((standard,index)=> (
<motion.div
key={index}
initial={{opacity: 0,y: 20}}
animate={{opacity: 1,y: 0}}
transition={{duration: 0.6,delay: index * 0.1}}
whileHover={{y: -5,scale: 1.02}}
className="bg-dark-800/30 backdrop-blur-sm rounded-2xl p-6 border border-dark-700/30 text-center hover:border-primary-500/30 transition-all duration-300"
>
<div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-purple rounded-xl mb-4 shadow-lg">
<SafeIcon icon={FiShield} className="w-6 h-6 text-white" />
</div>
<h3 className="text-sm font-semibold text-white">{standard}</h3>
</motion.div>
))}
</div>
</div>
</section>

{/* Contact for Legal */}
<section className="py-20 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-gradient-to-r from-primary-500/10 to-accent-purple/10 backdrop-blur-sm rounded-3xl p-8 border border-primary-500/20 text-center">
<h3 className="text-2xl lg:text-3xl font-bold text-primary-300 mb-4">
Questions About Our Policies?
</h3>
<p className="text-primary-200 mb-6 max-w-2xl mx-auto">
We're happy to discuss our security measures,compliance standards,and data handling practices in detail.
</p>
<motion.div
whileHover={{scale: 1.05}}
whileTap={{scale: 0.95}}
>
<Link
to="/contact"
className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300"
>
Contact Our Legal Team
</Link>
</motion.div>
</div>
</div>
</section>

{/* Footer Note */}
<section className="py-12 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="text-center text-gray-400">
<p className="text-sm">
Last updated: January 15,2025 • These policies are continuously updated to reflect current best practices and regulatory requirements.
</p>
</div>
</div>
</section>
</div>
);
};

export default Legal;